import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  memo,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { POPUP_OVERLAY_CONFIG } from "../../../../pixiSettings";
import { SOUND_NAME } from "../../../../sounds";
import anime from "animejs/lib/anime.es.js";
import FreeSpinZeroWinController from "./FreeSpinZeroWinController";
import { safeInit } from "../../../../utilities";
import { selectShowFreeSpinZeroWin } from "../../../../State/Slices/animationsSlice";
import { freeSpinOutroBet } from "../../../../Games/Shared/freeSpinOutroPopup";
import { soundManager } from "../../../../SoundManager";

/**
 * FreeSpinZeroWinDialog — the free-spin feature ended with nothing won.
 *
 * The `zero_win` skin of the shared win_celebration spine: a background and
 * BETTER LUCK NEXT TIME. No value to show, so no counter and no multiplier.
 *
 *   - The component NEVER unmounts. Visibility is alpha, which is what gives
 *     the spine's `out` a chance to play before the flag clears.
 *   - The overlay fades through `overlayRef`, driven by the controller.
 *   - Closing is the controller's job: `out`, then the flag, then the fade.
 */

const OVERLAY_BACKGROUND_COLOR = POPUP_OVERLAY_CONFIG.COLOR;
const OVERLAY_BACKGROUND_ALPHA = POPUP_OVERLAY_CONFIG.ALPHA;
const OVERLAY_FADE_DURATION = POPUP_OVERLAY_CONFIG.FADE_DURATION;

const FreeSpinZeroWinDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const show = useSelector(selectShowFreeSpinZeroWin);
  const totalWin = useSelector((state) => state.freeSpins?.totalWin ?? 0);
  // The SAME stake freeSpinOutroPopup thresholded on, so the popup can never
  // print a multiplier that contradicts its own reason for being open.
  const effectiveBet = useSelector(freeSpinOutroBet);

  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const dimensionsContext = useDimensions();
  const { dimensions, uiSymbolHeight: uiSymbolSize, device } = dimensionsContext;

  const [controllerReady, setControllerReady] = useState(false);
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
        complete: () => {
          if (typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
        alpha: 0,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
        complete: () => {
          setOverlayAlpha(0);
          if (typeof callback === "function") callback();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
    },
  }));

  // Initialize controller once — it lives for the session.
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      safeInit(() => {
        controllerRef.current = new FreeSpinZeroWinController(
          containerRef.current,
          dimensionsContext,
          dispatch,
          uiSymbolSize,
          overlayRef,
          t,
        );
        // ── Rendered, so the effect below can depend on it ──
        //
        // A ref is invisible to React. Keyed on [show] alone, the start effect
        // would fire once against a null ref and never run again, because
        // `show` does not change a second time.
        //
        // That is exactly what a completion reload does: the round is already
        // over in the store before this component mounts, so the dialog is
        // asked to open on the very first frame. FreeSpinTotalWinDialog
        // carries this flag for the same reason, and now that a short round
        // ends here instead, this one needs it too — without it a recovered
        // sub-1x round would leave the player stuck in the room.
        setControllerReady(true);
      });
    }
    return () => setControllerReady(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    controllerRef.current?.updateDimensions(dimensionsContext, uiSymbolSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device]);

  // The win and the stake it was played at, pushed in before the sequence
  // starts — the same order FreeSpinTotalWinDialog uses, so the value is on
  // screen from the first frame rather than popping in.
  useEffect(() => {
    if (controllerReady && controllerRef.current && show) {
      controllerRef.current.setTotalWinValue(totalWin, effectiveBet);
      controllerRef.current.startSequence();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, controllerReady]);

  const handleBackgroundClick = useCallback(
    (evt) => {
      if (evt?.preventDefault) evt.preventDefault();
      if (evt?.stopPropagation) evt.stopPropagation();
      if (!controllerRef.current || !show) return;
      if (controllerRef.current.handleUserClick()) {
        soundManager.play(SOUND_NAME.tap_to_continue);
      }
    },
    [show],
  );

  // Spacebar dismisses too, matching the intro.
  useEffect(() => {
    if (!show) return undefined;

    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " ") return;
      if (event.repeat) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (controllerRef.current?.handleUserClick()) {
        soundManager.play(SOUND_NAME.tap_to_continue);
      }
    };

    document.addEventListener("keydown", handleKeyDown, {
      capture: true,
      passive: false,
    });
    return () =>
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, [show]);

  useEffect(() => {
    return () => {
      controllerRef.current?.cleanup();
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  const containerPosition = useMemo(
    () => ({ x: dimensions.width / 2, y: dimensions.height / 2 }),
    [dimensions.width, dimensions.height],
  );

  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="free_spin_zero_win"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={9}
      ref={containerRef}
      // Gates the WHOLE subtree. The tap-to-continue container is eventMode
      // "static" with its own hit area, and closing only drops the spine's
      // alpha — so without this it stays invisible but still swallowing
      // pointer events, for every popup, for ever. Every Zeus popup carries
      // this guard; these five were missing it.
      eventMode={show ? "auto" : "none"}
    >
      {/* Drawn back to 0,0 so the rect covers the screen while the spine
          stays centred. */}
      <Graphics
        name="free_spin_zero_win_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={show ? "static" : "none"}
        pointerdown={handleBackgroundClick}
        // Clickable, but NEVER a pointer — only the tap word offers one.
        cursor="default"
      />
      {/* Spine, label and tap-to-continue are owned by the controller and
          pinned into the spine's own slots. */}
    </Container>
  );
};

export default memo(FreeSpinZeroWinDialog);
